import { ChatHistoryEntry, ChatMessageUI, ChatHistoryDocument, ChatAttachmentUI, ChatActionUI } from './chatbot.models';

export class ChatbotMapper {
	private static readonly DICT_KEY = 'inchat_button_dict';
	private static _globalButtonDict: Map<string, string> | null = null;

	private static get globalButtonDict(): Map<string, string> {
		if (!this._globalButtonDict) {
			this._globalButtonDict = new Map<string, string>();
			try {
				if (typeof localStorage !== 'undefined') {
					const stored = localStorage.getItem(this.DICT_KEY);
					if (stored) {
						const parsed = JSON.parse(stored);
						for (const key of Object.keys(parsed)) {
							this._globalButtonDict.set(key, parsed[key]);
						}
					}
				}
			} catch (e) {}
		}
		return this._globalButtonDict;
	}

	private static saveDict(): void {
		try {
			if (typeof localStorage !== 'undefined') {
				const obj: Record<string, string> = {};
				this.globalButtonDict.forEach((val, key) => {
					obj[key] = val;
				});
				localStorage.setItem(this.DICT_KEY, JSON.stringify(obj));
			}
		} catch (e) {}
	}

	public static registerButtonTitle(id: string, title: string): void {
		const cleanId = String(id).trim();
		const cleanTitle = String(title).trim();
		if (cleanId && cleanTitle && cleanId !== cleanTitle) {
			this.globalButtonDict.set(cleanId, cleanTitle);
			this.saveDict();
		}
	}

	private static readonly CONTEXT_KEY = 'inchat_context_cache';
	private static _globalContextList: Array<{
		text: string;
		contextId: string;
		contextText: string;
		contextName: string;
		timestamp: number;
	}> | null = null;

	private static get globalContextList(): Array<{
		text: string;
		contextId: string;
		contextText: string;
		contextName: string;
		timestamp: number;
	}> {
		if (!this._globalContextList) {
			this._globalContextList = [];
			try {
				if (typeof localStorage !== 'undefined') {
					const stored = localStorage.getItem(this.CONTEXT_KEY);
					if (stored) {
						this._globalContextList = JSON.parse(stored);
					}
				}
			} catch (e) {}
		}
		return this._globalContextList;
	}

	private static saveContextList(): void {
		try {
			if (typeof localStorage !== 'undefined') {
				const trimmed = this.globalContextList.slice(-100);
				localStorage.setItem(this.CONTEXT_KEY, JSON.stringify(trimmed));
			}
		} catch (e) {}
	}

	public static saveMessageContext(
		text: string,
		context: { contextId?: string; contextText?: string; contextName?: string; timestamp?: number }
	): void {
		const cleanText = (text || '').trim();
		if (!cleanText && !context.contextId) return;
		if (!context.contextText) return;

		this.globalContextList.push({
			text: cleanText,
			contextId: context.contextId || 'cached_ctx',
			contextText: context.contextText,
			contextName: context.contextName || 'Soporte',
			timestamp: context.timestamp || Date.now()
		});
		this.saveContextList();
	}

	public static findMessageContext(
		text?: string,
		dateObj?: Date | null
	): { contextId: string; contextText: string; contextName: string } | null {
		const cleanText = (text || '').trim();
		if (!cleanText) return null;

		const list = this.globalContextList;
		if (!list || list.length === 0) return null;

		const matches = list.filter((item) => item.text === cleanText);
		if (matches.length === 0) return null;

		if (dateObj && !isNaN(dateObj.getTime())) {
			const targetTime = dateObj.getTime();
			let closest = matches[0];
			let minDiff = Math.abs(closest.timestamp - targetTime);

			for (const m of matches) {
				const diff = Math.abs(m.timestamp - targetTime);
				if (diff < minDiff) {
					minDiff = diff;
					closest = m;
				}
			}
			return {
				contextId: closest.contextId,
				contextText: closest.contextText,
				contextName: closest.contextName
			};
		}

		const last = matches[matches.length - 1];
		return {
			contextId: last.contextId,
			contextText: last.contextText,
			contextName: last.contextName
		};
	}

	static mapHistoryDocumentsToMessages(documents: ChatHistoryDocument[]): ChatMessageUI[] {
		if (!documents || !Array.isArray(documents)) return [];

		const uiMessages: ChatMessageUI[] = [];
		let globalIndex = 0;
		// Using global static dictionary to preserve known button titles

		for (const doc of documents) {
			if (!doc || typeof doc !== 'object') continue;
			if (!doc.history || !Array.isArray(doc.history)) continue;

			for (const entry of doc.history) {
				// Populate dictionary with bot buttons before parsing the entry
				if (Array.isArray(entry.buttons_model)) {
					for (const b of entry.buttons_model) {
						if (b.id && b.title) {
							const cleanId = String(b.id).trim();
							const cleanTitle = String(b.title).trim();
							// Prevent overwriting a good translation with a raw ID
							if (!this.globalButtonDict.has(cleanId) || cleanTitle !== cleanId) {
								this.globalButtonDict.set(cleanId, cleanTitle);
							}
						}
					}
				}

				const msgs = this.mapSingleEntry(entry, globalIndex);
				uiMessages.push(...msgs);
				globalIndex++;
			}
		}

		// Avoid duplicates by id
		const uniqueMessages = new Map<string, ChatMessageUI>();
		for (const msg of uiMessages) {
			if (msg.id) {
				uniqueMessages.set(msg.id, msg);
			}
		}

		const finalMessages = Array.from(uniqueMessages.values()).sort((a, b) => {
			const timeA = a.dateObj ? a.dateObj.getTime() : null;
			const timeB = b.dateObj ? b.dateObj.getTime() : null;
			if (timeA !== null && timeB !== null && timeA !== timeB) return timeA - timeB;
			if (a.sender === 'user' && b.sender === 'bot') return -1;
			if (a.sender === 'bot' && b.sender === 'user') return 1;
			return (a.originalIndex ?? 0) - (b.originalIndex ?? 0);
		});

		// Deduplicate consecutive user messages with identical text or backendPayload
		const deduped: ChatMessageUI[] = [];
		for (const msg of finalMessages) {
			if (msg.sender === 'user' && deduped.length > 0) {
				const last = deduped[deduped.length - 1];
				if (last.sender === 'user') {
					if (
						msg.text === last.text ||
						(msg.backendPayload && msg.backendPayload === last.backendPayload)
					) {
						continue; // Skip duplicate
					}
				}
			}
			deduped.push(msg);
		}

		// Build map of all known messages for context resolution
		const messageMap = new Map<string, ChatMessageUI>();
		for (const msg of uiMessages) {
			if (msg.id) {
				messageMap.set(msg.id, msg);
			}
		}

		// Resolve referenced messages for contextId if contextText is missing
		for (const msg of deduped) {
			if (msg.contextId) {
				if (!msg.contextText) {
					const referenced = messageMap.get(msg.contextId);
					if (referenced) {
						msg.contextText = referenced.text || referenced.attachment?.name || 'Archivo adjunto';
						msg.contextName = referenced.sender === 'user' ? 'Tú' : 'Soporte';
					} else {
						const cached = this.findMessageContext(msg.text, msg.dateObj);
						if (cached) {
							msg.contextText = cached.contextText;
							msg.contextName = cached.contextName;
						} else {
							msg.contextText = 'Mensaje citado';
							msg.contextName = 'Soporte';
						}
					}
				}
				if (!msg.contextName) {
					const referenced = messageMap.get(msg.contextId);
					msg.contextName = referenced ? (referenced.sender === 'user' ? 'Tú' : 'Soporte') : 'Soporte';
				}
			} else if (msg.sender === 'user') {
				// If backend omitted context_id entirely, restore from persistent context cache
				const cached = this.findMessageContext(msg.text, msg.dateObj);
				if (cached) {
					msg.contextId = cached.contextId;
					msg.contextText = cached.contextText;
					msg.contextName = cached.contextName;
				}
			}
		}

		this.saveDict();
		return deduped;
	}

	private static generateDeterministicId(entry: ChatHistoryEntry, sender: 'user' | 'bot'): string {
		const text = sender === 'user' ? entry.text_user || '' : entry.text_model || '';
		const date = entry.date || '';
		const url = sender === 'user' ? entry.path_file_user || '' : entry.path_file_model || '';
		const type = entry.type || '';
		const contextId = entry.context_id || '';

		// Index-free ID
		const rawString = `${sender}_${text}_${date}_${url}_${type}_${contextId}`;

		let hash = 0;
		for (let i = 0; i < rawString.length; i++) {
			const char = rawString.charCodeAt(i);
			hash = (hash << 5) - hash + char;
			hash = hash & hash; // Convert to 32bit integer
		}
		return `${sender}_hash_${Math.abs(hash)}`;
	}

	private static mapSingleEntry(entry: ChatHistoryEntry, index: number): ChatMessageUI[] {
		const msgs: ChatMessageUI[] = [];
		if (!entry) return msgs;

		const dateObj = this.parseBackendDate(entry.date);
		const timeStr = dateObj ? this.formatTime(dateObj) : '';

		// Base and derived identifiers
		let finalUserId = entry.message_id_user;
		let finalBotId = entry.message_id_model;

		if (!finalUserId && finalBotId) {
			finalUserId = `user_derived_${finalBotId}`;
		}
		if (!finalBotId && finalUserId) {
			finalBotId = `bot_derived_${finalUserId}`;
		}

		// 1. User Message
		const hasUserText = !!(entry.text_user && entry.text_user.trim() !== '');
		const hasUserFile = !!entry.path_file_user;

		if (hasUserText || hasUserFile || entry.message_id_user) {
			const userMsg: ChatMessageUI = {
				id: finalUserId || this.generateDeterministicId(entry, 'user'),
				sender: 'user',
				time: timeStr,
				dateObj: dateObj || undefined,
				status: 'sent',
				flowType: entry.type,
				originalIndex: index,
				contextId: entry.context_id,
				contextText: entry.context_text,
				contextName: entry.context_name
			};

			if (hasUserText) {
				const rawText = entry.text_user!.trim();
				// Translate the ID back to the human-readable Title using the dictionary
				let translatedTitle = this.globalButtonDict.get(rawText);

				// Fallback: The backend sometimes strips suffixes (e.g. 'support_advisor_general legal' -> 'support_advisor_general')
				if (!translatedTitle) {
					for (const [key, value] of this.globalButtonDict.entries()) {
						if (key.startsWith(rawText + ' ')) {
							translatedTitle = value;
							break;
						}
					}
				}

				if (translatedTitle && translatedTitle !== rawText) {
					userMsg.text = translatedTitle;
					userMsg.backendPayload = rawText; // Preserve the real ID for tracking
				} else {
					userMsg.text = rawText;
					userMsg.backendPayload = rawText; // Add this so deduping works even if translation fails!
				}
			}

			if (hasUserFile) {
				userMsg.attachment = this.createAttachment(entry.path_file_user!, entry.real_name_file_user);
			}
			msgs.push(userMsg);
		}

		// 2. Bot Message
		const hasBotText = !!(entry.text_model && entry.text_model.trim() !== '');
		const hasBotFile = !!entry.path_file_model;
		const hasBotButtons = Array.isArray(entry.buttons_model) && entry.buttons_model.length > 0;

		if (hasBotText || hasBotFile || hasBotButtons || entry.message_id_model) {
			const botMsg: ChatMessageUI = {
				id: finalBotId || this.generateDeterministicId(entry, 'bot'),
				sender: 'bot',
				time: timeStr,
				dateObj: dateObj || undefined,
				flowType: entry.type,
				originalIndex: index,
				contextId: entry.context_id,
				contextText: entry.context_text,
				contextName: entry.context_name
			};

			if (hasBotText) botMsg.text = entry.text_model;
			if (hasBotFile) {
				botMsg.attachment = this.createAttachment(entry.path_file_model!, entry.real_name_file_model);
			}
			if (hasBotButtons) {
				botMsg.actions = entry.buttons_model!.map((b) => ({
					id: String(b.id),
					title: String(b.title),
					style: 'orange-button'
				}));
			}
			msgs.push(botMsg);
		}

		return msgs;
	}

	static parseBackendDate(dateStr?: string): Date | null {
		if (!dateStr || typeof dateStr !== 'string') return null;

		const parts = dateStr.split(', ');
		if (parts.length === 2) {
			const dateParts = parts[0].split('/');
			if (dateParts.length === 3) {
				const day = parseInt(dateParts[0], 10);
				const month = parseInt(dateParts[1], 10) - 1;
				const year = parseInt(dateParts[2], 10);

				const timeParts = parts[1].split(':');
				if (timeParts.length >= 2) {
					const hour = parseInt(timeParts[0], 10);
					const min = parseInt(timeParts[1], 10);
					const sec = timeParts.length === 3 ? parseInt(timeParts[2], 10) : 0;

					const date = new Date(year, month, day, hour, min, sec);
					if (
						!isNaN(date.getTime()) &&
						date.getFullYear() === year &&
						date.getMonth() === month &&
						date.getDate() === day &&
						date.getHours() === hour &&
						date.getMinutes() === min &&
						date.getSeconds() === sec
					) {
						return date;
					}
				}
			}
		}

		const fallbackDate = new Date(dateStr);
		if (!isNaN(fallbackDate.getTime())) {
			return fallbackDate;
		}

		return null;
	}

	private static formatTime(date: Date): string {
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}

	private static createAttachment(rawUrl: string, realName?: string): ChatAttachmentUI {
		let cleanUrl = rawUrl;
		const queryIndex = cleanUrl.indexOf('?');
		if (queryIndex !== -1) cleanUrl = cleanUrl.substring(0, queryIndex);
		const hashIndex = cleanUrl.indexOf('#');
		if (hashIndex !== -1) cleanUrl = cleanUrl.substring(0, hashIndex);

		const urlLower = cleanUrl.toLowerCase();
		let type: 'image' | 'document' | 'audio' = 'document';

		if (urlLower.match(/\.(jpeg|jpg|gif|png|webp|svg)$/)) {
			type = 'image';
		} else if (urlLower.match(/\.(mp3|wav|ogg|webm)$/)) {
			type = 'audio';
		}

		let name = realName;
		if (!name) {
			const parts = cleanUrl.split('/');
			const lastPart = parts[parts.length - 1];
			try {
				name = decodeURIComponent(lastPart) || 'archivo_adjunto';
			} catch {
				name = lastPart || 'archivo_adjunto';
			}
		}

		return { type, url: rawUrl, name };
	}

	public static mapJsonModelToBotMessages(jsonModel: unknown[], flowType?: string): ChatMessageUI[] {
		if (!Array.isArray(jsonModel) || jsonModel.length === 0) return [];
		const now = new Date();
		const timeStr = this.formatTime(now);
		const botMessages: ChatMessageUI[] = [];

		jsonModel.forEach((item: any, idx: number) => {
			if (!item || typeof item !== 'object') return;
			const text = (item.text || '').trim();
			const buttons = Array.isArray(item.buttons) ? item.buttons : [];

			if (text || buttons.length > 0) {
				buttons.forEach((b: any) => {
					if (b && b.id && b.title) {
						this.registerButtonTitle(String(b.id).trim(), String(b.title).trim());
					}
				});

				const actions: ChatActionUI[] = buttons.map((b: any) => ({
					id: String(b.id),
					title: String(b.title),
					style: 'orange-button'
				}));

				botMessages.push({
					id: `bot_immediate_${Date.now()}_${idx}`,
					sender: 'bot',
					text: text || undefined,
					actions: actions.length > 0 ? actions : undefined,
					time: timeStr,
					dateObj: now,
					status: 'sent',
					flowType: flowType,
					optimistic: true
				});
			}
		});

		return botMessages;
	}

	static normalizePhoneNumber(phone?: string): string {
		if (!phone || typeof phone !== 'string') return '';

		const raw = phone.trim();
		if (raw === '') return '';

		const hasPlus = raw.startsWith('+');
		const has00 = raw.startsWith('00');

		let digits = raw.replace(/\D/g, '');
		if (!digits) return '';

		if (has00) {
			digits = digits.replace(/^00/, '');
		}

		// Reject phone numbers with long repeated digit runs (e.g. 999999999, 5100000000)
		if (/(\d)\1{5,}/.test(digits)) {
			return '';
		}

		if (digits.length >= 7 && digits.length <= 15) {
			return digits;
		}

		return '';
	}

	static normalizeDocumentNumber(doc?: string): string {
		if (!doc || typeof doc !== 'string') return '';
		// Strip separators (spaces, dots, dashes, slashes, underscores) and uppercase the letters
		return doc.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
	}
}
